import React, { FC, useContext, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Svg } from "@natera/material/lib/svg";

import LinkActionCard from "@app/postBill/components/linkActionCard";

import phoneSVG from "@assets/svg/icons/phone.svg";
import helpSVG from "@assets/svg/icons/help.svg";
import acknowledgements from "@etc/acknowledgements.json";
import config from "@etc/config.json";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { UserContext } from "@app/provider";

import "./billingCenterHelp.scss";
import { HEAP_EVENTS } from "@app/provider/types";
import { LinkHeap } from "@app/components";

const messages = defineMessages({
  billingCenterHelpContactTitle: {
    id: "billingCenterHelpContactTitle",
    defaultMessage: "We're here to help!",
  },
  billingCenterHelpContactBody: {
    id: "billingCenterHelpContactBody",
    defaultMessage:
      "If you need any further help or information, please contact us.",
  },
  billingCenterHelpContactButton: {
    id: "billingCenterHelpContactButton",
    defaultMessage: "Contact us",
  },
  billingCenterHelpPricingTitle: {
    id: "billingCenterHelpPricingTitle",
    defaultMessage: "Pricing and billing FAQs",
  },
  billingCenterHelpPricingBody: {
    id: "billingCenterHelpPricingBody",
    defaultMessage:
      "Select your test type below to view pricing and billing questions specific to your test:",
  },
  billingCenterHelpPricingCardWomensHealthTitle: {
    id: "billingCenterHelpPricingCardWomensHealthTitle",
    defaultMessage: "Women's health",
  },
  billingCenterHelpPricingCardWomensHealthBody: {
    id: "billingCenterHelpPricingCardWomensHealthBody",
    defaultMessage:
      "NIPT, carrier screening, hereditary cancer screening ordered through your OBGYN, and other reproductive health tests.",
  },
  billingCenterHelpPricingCardOrganHealthTitle: {
    id: "billingCenterHelpPricingCardOrganHealthTitle",
    defaultMessage: "Organ health",
  },
  billingCenterHelpPricingCardOrganHealthBody: {
    id: "billingCenterHelpPricingCardOrganHealthBody",
    defaultMessage: "Kidney gene panel or transplant assessment.",
  },
  billingCenterHelpPricingCardOncologyTitle: {
    id: "billingCenterHelpPricingCardOncologyTitle",
    defaultMessage: "Oncology",
  },
  billingCenterHelpPricingCardOncologyBody: {
    id: "billingCenterHelpPricingCardOncologyBody",
    defaultMessage:
      "Residual disease test, hereditary cancer test, or tumor genomic profile.",
  },
});

const BillingCenterHelp: FC = () => {
  const intl = useIntl();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const { uppUser } = useContext(UserContext);

  useEffect(() => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_view_billing_help_tab,
    });
  }, []);

  return (
    <div className="billing-center-help">
      <div className="billing-center-help__container">
        <div className="billing-center-help__container__section">
          <div className="billing-center-help__container__section__title">
            <Svg content={phoneSVG} />
            <span>
              {intl.formatMessage(messages.billingCenterHelpContactTitle)}
            </span>
          </div>
          <span>
            {intl.formatMessage(messages.billingCenterHelpContactBody)}
          </span>
          <LinkHeap
            className="billing-center-help__container__section__button"
            to={acknowledgements.links.contactUs}
            key="contactUs"
            target="_blank"
            rel="noreferrer"
            heapEventName={HEAP_EVENTS.upp_click_billing_help_contact_us}
            heapEventProps={{ identity: uppUser?.patientUID }}
          >
            {intl.formatMessage(messages.billingCenterHelpContactButton)}
          </LinkHeap>
        </div>
        <div className="billing-center-help__container__section">
          <div className="billing-center-help__container__section__title">
            <Svg content={helpSVG} />
            <span>
              {intl.formatMessage(messages.billingCenterHelpPricingTitle)}
            </span>
          </div>
          <span>
            {intl.formatMessage(messages.billingCenterHelpPricingBody)}
          </span>
          <div className="billing-center-help__container__section__items">
            <LinkActionCard
              title={intl.formatMessage(
                messages.billingCenterHelpPricingCardWomensHealthTitle
              )}
              body={intl.formatMessage(
                messages.billingCenterHelpPricingCardWomensHealthBody
              )}
              href={config.links.WOMENS_HEALTH_PRICING_BILLING}
              heapEvent={HEAP_EVENTS.upp_click_billing_help_wh}
            />
            <LinkActionCard
              title={intl.formatMessage(
                messages.billingCenterHelpPricingCardOrganHealthTitle
              )}
              body={intl.formatMessage(
                messages.billingCenterHelpPricingCardOrganHealthBody
              )}
              href={config.links.ORGAN_HEALTH_PRICING_BILLING}
              heapEvent={HEAP_EVENTS.upp_click_billing_help_oh}
            />
            <LinkActionCard
              title={intl.formatMessage(
                messages.billingCenterHelpPricingCardOncologyTitle
              )}
              body={intl.formatMessage(
                messages.billingCenterHelpPricingCardOncologyBody
              )}
              href={config.links.ONCOLOGY_PRICING_BILLING}
              heapEvent={HEAP_EVENTS.upp_click_billing_help_onc}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingCenterHelp;
